import {
  useId,
  useRef,
  useState,
  type ChangeEvent,
  type FormEvent,
  type ReactElement,
} from 'react';
import {
  useVoyageAppConfig,
  useDeleteVoyageAppConfig,
  voyageModelLabel,
  VoyageNotConfiguredError,
  type VoyageAppConfig,
  type VoyageAppConfigPutResponse,
} from '@/lib/voyage';
import {
  formatBytes,
  KnowledgeAlreadyUploadedError,
  KnowledgeFileTooLargeError,
  KnowledgePdfWithoutTextError,
  useDeleteDocument,
  useDocuments,
  useSearchKnowledge,
  useUploadPdf,
  type KnowledgeDocument,
} from '@/lib/knowledge';
import { VoyageConfigForm } from './VoyageConfigForm';
import { ConfirmDialog } from '@/features/settings/ConfirmDialog';

/**
 * Página `/biblioteca` — RAG sobre PDFs personales.
 *
 * Tres macro-estados:
 *  1. !voyage_credentials.configured  →  <VoyageConfigForm /> en modo "create".
 *  2. configured  + sin documentos    →  tarjeta config + zona vacía + uploader + buscador deshabilitado.
 *  3. configured  + con documentos    →  igual que (2) + grid de documentos + buscador habilitado.
 *
 * Banners para feedback global de mutations (subida ok, borrado, errores 409
 * / 413 / 422 / 503). `role="status"` (no-blocking) o `role="alert"` (blocking)
 * según severidad — convención compartida con `AnthropicSettings`.
 */
export function LibraryPage(): ReactElement {
  const voyageQuery = useVoyageAppConfig();
  const deleteVoyageMutation = useDeleteVoyageAppConfig();

  const [savedBanner, setSavedBanner] = useState<
    { kind: 'success' | 'warning' | 'error'; message: string } | null
  >(null);
  const [confirmDeleteVoyageOpen, setConfirmDeleteVoyageOpen] = useState(false);
  const [editingVoyage, setEditingVoyage] = useState(false);

  function handleDeleteVoyage() {
    deleteVoyageMutation.mutate(undefined, {
      onSuccess: () => {
        setConfirmDeleteVoyageOpen(false);
        setEditingVoyage(false);
        setSavedBanner({
          kind: 'success',
          message:
            'Configuración de Voyage borrada. Tus documentos siguen guardados pero no podrás buscar ni subir nuevos hasta volver a configurar la API key.',
        });
      },
    });
  }

  const voyageConfig: VoyageAppConfig | undefined = voyageQuery.data;
  const configured: boolean = voyageConfig?.configured ?? false;

  return (
    <section aria-labelledby="library-title" className="space-y-6">
      <header>
        <h1
          id="library-title"
          className="text-2xl font-semibold tracking-tight text-slate-900"
        >
          Biblioteca
        </h1>
        <p className="mt-1 text-sm text-slate-600">
          Sube tus libros y artículos de triatlón en PDF. El coach los citará
          al generar planes y podrás buscarlos por contenido.
        </p>
      </header>

      {savedBanner && (
        <div
          role={savedBanner.kind === 'error' ? 'alert' : 'status'}
          className={
            savedBanner.kind === 'success'
              ? 'rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-900'
              : savedBanner.kind === 'warning'
                ? 'rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900'
                : 'rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-800'
          }
        >
          {savedBanner.message}
        </div>
      )}

      {voyageQuery.isLoading && (
        <p role="status" aria-live="polite" className="text-slate-700">
          Cargando configuración de Voyage…
        </p>
      )}

      {voyageQuery.isError && (
        <p
          role="alert"
          className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-800"
        >
          No se pudo cargar la configuración de Voyage.{' '}
          {voyageQuery.error?.message ?? ''}
        </p>
      )}

      {/* Estado 1: sin configurar */}
      {voyageConfig && !configured && (
        <VoyageConfigForm
          mode="create"
          onSaved={() => {
            setSavedBanner({
              kind: 'success',
              message:
                'API key de Voyage guardada. Ya puedes subir PDFs y buscar en tu biblioteca.',
            });
          }}
        />
      )}

      {/* Estado 2/3: configurado */}
      {voyageConfig && configured && (
        <>
          <VoyageConfigCard
            voyageConfig={voyageConfig}
            editing={editingVoyage}
            onEdit={() => setEditingVoyage(true)}
            onCancelEdit={() => setEditingVoyage(false)}
            onAskDelete={() => setConfirmDeleteVoyageOpen(true)}
            onSaved={(response) => {
              setEditingVoyage(false);
              if (response.apiKeyChanged) {
                setSavedBanner({
                  kind: 'success',
                  message: 'Nueva API key de Voyage guardada.',
                });
              } else {
                setSavedBanner({
                  kind: 'success',
                  message: 'Configuración de Voyage actualizada.',
                });
              }
            }}
          />

          <DocumentsSection
            onBanner={setSavedBanner}
            onClearBanner={() => setSavedBanner(null)}
          />

          <SearchSection />
        </>
      )}

      <ConfirmDialog
        open={confirmDeleteVoyageOpen}
        title="¿Borrar configuración de Voyage?"
        description={
          <>
            Se borrará tu API key cifrada. Tus documentos y embeddings
            <strong> seguirán guardados</strong>, pero no podrás generar nuevos
            embeddings ni hacer búsquedas hasta volver a configurar una key.
          </>
        }
        confirmLabel={deleteVoyageMutation.isPending ? 'Borrando…' : 'Borrar config'}
        destructive
        isPending={deleteVoyageMutation.isPending}
        onConfirm={handleDeleteVoyage}
        onCancel={() => setConfirmDeleteVoyageOpen(false)}
      />
    </section>
  );
}

// ---------------------------------------------------------------------------
// Tarjeta de configuración Voyage
// ---------------------------------------------------------------------------

function VoyageConfigCard({
  voyageConfig,
  editing,
  onEdit,
  onCancelEdit,
  onAskDelete,
  onSaved,
}: {
  voyageConfig: VoyageAppConfig;
  editing: boolean;
  onEdit: () => void;
  onCancelEdit: () => void;
  onAskDelete: () => void;
  onSaved: (response: VoyageAppConfigPutResponse) => void;
}): ReactElement {
  if (editing) {
    // `exactOptionalPropertyTypes` impide pasar `undefined` para una prop
    // opcional; sólo añadimos `initialModelDefault` cuando hay valor.
    return voyageConfig.modelDefault ? (
      <VoyageConfigForm
        initialModelDefault={voyageConfig.modelDefault}
        mode="edit"
        onSaved={onSaved}
        onCancel={onCancelEdit}
      />
    ) : (
      <VoyageConfigForm
        mode="edit"
        onSaved={onSaved}
        onCancel={onCancelEdit}
      />
    );
  }

  return (
    <section
      aria-labelledby="voyage-config-title"
      className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm"
    >
      <h2
        id="voyage-config-title"
        className="text-lg font-semibold text-slate-900"
      >
        Configuración de Voyage
      </h2>
      <dl className="mt-3 space-y-2 text-sm">
        <div className="grid grid-cols-[8rem_1fr] gap-2">
          <dt className="text-slate-600">Modelo</dt>
          <dd className="text-slate-900">
            {voyageConfig.modelDefault
              ? voyageModelLabel(voyageConfig.modelDefault)
              : '—'}
          </dd>
        </div>
        {voyageConfig.updatedAt && (
          <div className="grid grid-cols-[8rem_1fr] gap-2">
            <dt className="text-slate-600">Actualizado</dt>
            <dd className="text-slate-900">{formatDate(voyageConfig.updatedAt)}</dd>
          </div>
        )}
      </dl>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={onEdit}
          className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-2"
        >
          Editar
        </button>
        <button
          type="button"
          onClick={onAskDelete}
          className="rounded-md border border-rose-300 bg-white px-4 py-2 text-sm font-medium text-rose-700 hover:bg-rose-50 focus:outline-none focus:ring-2 focus:ring-rose-600 focus:ring-offset-2"
        >
          Borrar config
        </button>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Sección documentos: lista + uploader
// ---------------------------------------------------------------------------

function DocumentsSection({
  onBanner,
  onClearBanner,
}: {
  onBanner: (b: { kind: 'success' | 'warning' | 'error'; message: string }) => void;
  onClearBanner: () => void;
}): ReactElement {
  const documentsQuery = useDocuments();
  const uploadMutation = useUploadPdf();
  const deleteMutation = useDeleteDocument();

  const fileInputId = useId();
  const fileHelpId = useId();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<KnowledgeDocument | null>(
    null,
  );

  function onFileChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0] ?? null;
    setSelectedFile(file);
    // Validación cliente rápida del tamaño máximo (30 MB).
    if (file && file.size > 30 * 1024 * 1024) {
      onBanner({
        kind: 'error',
        message: `PDF > 30MB (${formatBytes(file.size)}). Comprime o divide el documento.`,
      });
    } else {
      onClearBanner();
    }
  }

  function onUploadSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!selectedFile) return;
    if (selectedFile.size > 30 * 1024 * 1024) return; // ya avisado arriba

    uploadMutation.mutate(selectedFile, {
      onSuccess: (resp) => {
        onBanner({
          kind: 'success',
          message: `«${resp.document.title}» subido (${resp.chunks} chunks indexados).`,
        });
        setSelectedFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
      },
      onError: (err) => {
        if (err instanceof KnowledgeAlreadyUploadedError) {
          onBanner({
            kind: 'warning',
            message: 'Este PDF ya estaba subido a tu biblioteca.',
          });
        } else if (err instanceof KnowledgeFileTooLargeError) {
          onBanner({ kind: 'error', message: 'PDF > 30MB.' });
        } else if (err instanceof KnowledgePdfWithoutTextError) {
          onBanner({
            kind: 'error',
            message:
              'El PDF no contiene texto extraíble (parece ser sólo imágenes escaneadas). Pásalo por OCR e inténtalo de nuevo.',
          });
        } else if (err instanceof VoyageNotConfiguredError) {
          onBanner({
            kind: 'error',
            message: 'Configura Voyage antes de subir documentos.',
          });
        } else {
          onBanner({
            kind: 'error',
            message: err.message || 'No se pudo subir el PDF.',
          });
        }
      },
    });
  }

  function handleConfirmDelete() {
    if (!confirmDelete) return;
    const doc = confirmDelete;
    deleteMutation.mutate(doc.id, {
      onSuccess: () => {
        setConfirmDelete(null);
        onBanner({
          kind: 'success',
          message: `«${doc.title}» borrado de la biblioteca.`,
        });
      },
      onError: (err) => {
        onBanner({
          kind: 'error',
          message: err.message || 'No se pudo borrar el documento.',
        });
      },
    });
  }

  const documents = documentsQuery.data ?? [];

  return (
    <section
      aria-labelledby="documents-title"
      className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm"
    >
      <h2 id="documents-title" className="text-lg font-semibold text-slate-900">
        Documentos
      </h2>

      {/* Uploader */}
      <form onSubmit={onUploadSubmit} className="mt-4 space-y-2" noValidate>
        <label
          htmlFor={fileInputId}
          className="block text-sm font-medium text-slate-800"
        >
          Subir PDF
        </label>
        <div className="flex flex-wrap items-center gap-3">
          <input
            id={fileInputId}
            ref={fileInputRef}
            type="file"
            accept="application/pdf,.pdf"
            onChange={onFileChange}
            aria-describedby={fileHelpId}
            disabled={uploadMutation.isPending}
            className="block max-w-full text-sm text-slate-900 file:mr-3 file:cursor-pointer file:rounded-md file:border-0 file:bg-teal-600 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-white hover:file:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
          />
          <button
            type="submit"
            disabled={
              !selectedFile ||
              uploadMutation.isPending ||
              selectedFile.size > 30 * 1024 * 1024
            }
            aria-busy={uploadMutation.isPending}
            className="rounded-md bg-teal-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {uploadMutation.isPending ? 'Subiendo…' : 'Subir'}
          </button>
        </div>
        <p id={fileHelpId} className="text-xs text-slate-500">
          PDFs de hasta 30 MB. El texto se trocea en chunks, se generan
          embeddings con Voyage y se guarda todo en local. Subir un libro de
          ~300 páginas tarda unos segundos.
        </p>
        {selectedFile && (
          <p className="text-xs text-slate-600">
            Seleccionado:{' '}
            <strong className="text-slate-800">{selectedFile.name}</strong>{' '}
            ({formatBytes(selectedFile.size)})
          </p>
        )}
        {uploadMutation.isPending && (
          <div
            role="progressbar"
            aria-label="Subiendo PDF"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuetext="Subiendo PDF, esto puede tardar unos segundos"
            className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-slate-200"
          >
            <div className="h-full w-1/3 animate-pulse rounded-full bg-teal-600" />
          </div>
        )}
      </form>

      {/* Lista de documentos */}
      <div className="mt-6">
        {documentsQuery.isLoading && (
          <p role="status" aria-live="polite" className="text-sm text-slate-600">
            Cargando documentos…
          </p>
        )}

        {documentsQuery.isError && (
          <p
            role="alert"
            className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-800"
          >
            No se pudieron cargar los documentos.{' '}
            {documentsQuery.error?.message ?? ''}
          </p>
        )}

        {documentsQuery.isSuccess && documents.length === 0 && (
          <p className="text-sm text-slate-600">
            Aún no has subido ningún PDF. Empieza con un libro de referencia
            (Friel, Allen & Coggan, etc.).
          </p>
        )}

        {documentsQuery.isSuccess && documents.length > 0 && (
          <ul
            aria-label="Documentos subidos"
            className="grid grid-cols-1 gap-3 sm:grid-cols-2"
          >
            {documents.map((doc) => (
              <li
                key={doc.id}
                className="rounded-md border border-slate-200 bg-slate-50 p-4"
              >
                <h3 className="text-sm font-semibold text-slate-900">
                  {doc.title}
                </h3>
                <dl className="mt-2 space-y-1 text-xs text-slate-600">
                  <div>
                    <dt className="inline font-medium text-slate-500">
                      Chunks:{' '}
                    </dt>
                    <dd className="inline text-slate-700">{doc.totalChunks}</dd>
                  </div>
                  {typeof doc.totalPages === 'number' && doc.totalPages > 0 && (
                    <div>
                      <dt className="inline font-medium text-slate-500">
                        Páginas:{' '}
                      </dt>
                      <dd className="inline text-slate-700">{doc.totalPages}</dd>
                    </div>
                  )}
                  <div>
                    <dt className="inline font-medium text-slate-500">
                      Tamaño:{' '}
                    </dt>
                    <dd className="inline text-slate-700">
                      {formatBytes(doc.bytesSize)}
                    </dd>
                  </div>
                  <div>
                    <dt className="inline font-medium text-slate-500">
                      Subido:{' '}
                    </dt>
                    <dd className="inline text-slate-700">
                      {formatDate(doc.uploadedAt)}
                    </dd>
                  </div>
                </dl>
                <button
                  type="button"
                  onClick={() => setConfirmDelete(doc)}
                  className="mt-3 rounded-md border border-rose-300 bg-white px-3 py-1.5 text-xs font-medium text-rose-700 hover:bg-rose-50 focus:outline-none focus:ring-2 focus:ring-rose-600 focus:ring-offset-2"
                >
                  Borrar
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <ConfirmDialog
        open={confirmDelete !== null}
        title="¿Borrar este documento?"
        description={
          confirmDelete ? (
            <>
              Se borrarán «{confirmDelete.title}» y todos sus chunks (
              {confirmDelete.totalChunks}). Esta acción no se puede deshacer.
            </>
          ) : (
            ''
          )
        }
        confirmLabel={deleteMutation.isPending ? 'Borrando…' : 'Borrar documento'}
        destructive
        isPending={deleteMutation.isPending}
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmDelete(null)}
      />
    </section>
  );
}

// ---------------------------------------------------------------------------
// Sección búsqueda
// ---------------------------------------------------------------------------

function SearchSection(): ReactElement {
  const searchInputId = useId();
  const [draftQuery, setDraftQuery] = useState('');
  const [activeQuery, setActiveQuery] = useState('');

  const searchQuery = useSearchKnowledge(activeQuery, 8, {
    enabled: activeQuery.trim().length > 0,
  });

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const trimmed = draftQuery.trim();
    if (trimmed.length === 0) return;
    setActiveQuery(trimmed);
  }

  const results = searchQuery.data ?? [];

  return (
    <section
      aria-labelledby="search-title"
      className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm"
    >
      <h2 id="search-title" className="text-lg font-semibold text-slate-900">
        Buscar en mi biblioteca
      </h2>
      <p className="mt-1 text-sm text-slate-600">
        Consulta semántica sobre el contenido de tus PDFs.
      </p>

      <form onSubmit={onSubmit} className="mt-4 flex flex-wrap items-stretch gap-2">
        <label htmlFor={searchInputId} className="sr-only">
          Búsqueda
        </label>
        <input
          id={searchInputId}
          type="search"
          value={draftQuery}
          onChange={(e) => setDraftQuery(e.target.value)}
          placeholder="p. ej. zona 2 en pretemporada"
          className="flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-teal-600 focus:outline-none focus:ring-2 focus:ring-teal-600"
        />
        <button
          type="submit"
          disabled={draftQuery.trim().length === 0 || searchQuery.isFetching}
          aria-busy={searchQuery.isFetching}
          className="rounded-md bg-teal-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {searchQuery.isFetching ? 'Buscando…' : 'Buscar'}
        </button>
      </form>

      <div className="mt-6">
        {searchQuery.isError && (
          <p
            role="alert"
            className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-800"
          >
            {searchQuery.error instanceof VoyageNotConfiguredError
              ? 'Configura Voyage para poder buscar.'
              : (searchQuery.error?.message ?? 'No se pudo completar la búsqueda.')}
          </p>
        )}

        {searchQuery.isSuccess &&
          activeQuery.length > 0 &&
          results.length === 0 && (
            <p className="text-sm text-slate-600">
              Sin resultados para «{activeQuery}». Prueba con otras palabras
              clave o sube más documentos.
            </p>
          )}

        {results.length > 0 && (
          <ul aria-label="Resultados de búsqueda" className="space-y-3">
            {results.map((r) => (
              <li
                key={r.chunkId}
                className="rounded-md border border-slate-200 bg-slate-50 p-4"
              >
                <header className="flex flex-wrap items-baseline justify-between gap-2">
                  <h3 className="text-sm font-semibold text-slate-900">
                    {r.documentTitle}
                    {typeof r.pageStart === 'number' && (
                      <span className="ml-2 text-xs font-normal text-slate-500">
                        {r.pageEnd && r.pageEnd !== r.pageStart
                          ? `p. ${r.pageStart}-${r.pageEnd}`
                          : `p. ${r.pageStart}`}
                      </span>
                    )}
                  </h3>
                  <span
                    className="text-xs font-medium text-slate-600"
                    aria-label={`Puntuación de similitud: ${formatScore(r.score)}`}
                  >
                    score {formatScore(r.score)}
                  </span>
                </header>
                <p className="mt-2 text-sm text-slate-700">{snippet(r.text)}</p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Helpers locales de presentación
// ---------------------------------------------------------------------------

function formatDate(iso: string): string {
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return iso;
  return new Date(iso).toLocaleString('es-ES', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatScore(s: number): string {
  if (!Number.isFinite(s)) return '—';
  return s.toFixed(3);
}

/** Recorta un chunk a ~320 chars para previsualización en la lista. */
function snippet(text: string, max = 320): string {
  const trimmed = text.trim().replace(/\s+/g, ' ');
  if (trimmed.length <= max) return trimmed;
  return `${trimmed.slice(0, max).trim()}…`;
}
